import React, { useEffect, useState } from "react";
import { FileSearch, Shield, Edit, LayoutDashboard } from "lucide-react";
import Logo from "../assets/images/NabsLogoOld.png";
import AuthBanner from "../assets/images/AuthBanner.jpg";

const AuthFeaturesSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    {
      icon: <FileSearch className="w-5 h-5" />,
      text: "Retrieve notices automatically from govt. portals.",
      delay: 0.2,
    },
    {
      icon: <Shield className="w-5 h-5" />,
      text: "Verified case laws & legal references with RAG tech.",
      delay: 0.4,
    },
    {
      icon: <Edit className="w-5 h-5" />,
      text: "Auto-generate accurate draft responses to tax notices.",
      delay: 0.6,
    },
    {
      icon: <LayoutDashboard className="w-5 h-5" />,
      text: "Manage all clients notices with one smart dashboard.",
      delay: 0.8,
    },
  ];

  return (
    <>
      <style>{`
        .fade-in-up {
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        .fade-in-down {
          opacity: 0;
          transform: translateY(-30px);
          animation: fadeInDown 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        .fade-in-left {
          opacity: 0;
          transform: translateX(-50px);
          animation: fadeInLeft 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        .smooth-hover {
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        .smooth-hover:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
          backdrop-filter: blur(10px);
        }

        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInDown {
          0% {
            opacity: 0;
            transform: translateY(-30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInLeft {
          0% {
            opacity: 0;
            transform: translateX(-50px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .feature-item {
          opacity: 0;
          transform: translateX(-50px);
          animation: fadeInLeft 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        .feature-item-0 { 
          animation-delay: 0.2s; 
        }
        .feature-item-1 { 
          animation-delay: 0.4s; 
        }
        .feature-item-2 { 
          animation-delay: 0.6s; 
        }
        .feature-item-3 { 
          animation-delay: 0.8s; 
        }

        .title-animation {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s forwards;
        }

        .logo-container {
          opacity: 0;
          transform: translateY(-30px);
          animation: fadeInDown 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        .tagline {
          opacity: 0;
          animation: fadeInUp 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.4s forwards;
        }

        .banner-container {
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 1.0s forwards;
        }

        .features-container {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s forwards;
        }
      `}</style>

      <div className="flex flex-col items-center h-full bg-white p-8 relative overflow-hidden">
        <div className="relative z-10 px-16">
          {/* Logo Section */}
          <div
            className={`text-center mb-4 ${isVisible ? "logo-container" : ""}`}
          >
            <div className="flex justify-center">
              <img
                src={Logo}
                className="w-[250px] nabsAuthLogo"
                draggable={false}
              />
            </div>
            <p
              className={`text-gray-600 text-sm tracking-wider mt-2 ${
                isVisible ? "tagline" : ""
              }`}
            >
              Automate • Accelerate • Achieve
            </p>
          </div>

          {/* Features List */}
          <div
            className={`space-y-4 mb-4 bg-[#89CFF0]/8 rounded-3xl py-4 ${
              isVisible ? "features-container" : ""
            }`}
          >
            <h5
              className={`text-lg text-gray-800 text-center mb-4 leading-tight font-semibold ${
                isVisible ? "title-animation" : ""
              }`}
            >
              Simplify Your Tax Notice Management
            </h5>

            {features.map((feature, index) => (
              <div
                key={index}
                className={`flex items-center bg-[#7393B3]/10 backdrop-blur-sm rounded-full shadow-sm border border-blue-100 pr-4 smooth-hover ${
                  isVisible ? `feature-item feature-item-${index}` : ""
                }`}
              >
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 mr-4 flex-shrink-0 transition-all duration-300">
                  {feature.icon}
                </div>
                <p className="text-gray-700 font-medium text-sm leading-relaxed">
                  {feature.text}
                </p>
              </div>
            ))}
          </div>

          <div
            className={`h-[260px] w-[100%] relative overflow-hidden mb-4 ${
              isVisible ? "banner-container" : ""
            }`}
          >
            <img
              src={AuthBanner}
              className=" object-cover object-top-right absolute inset-0 w-full h-full rounded-3xl shadow-lg"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthFeaturesSection;
